import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal, { modalStyles as m } from "./Modal";
import {
  phpOrderList,
  phpOrderDetails,
  phpItemLevelRefund,
  phpPartialRefund,
  phpVoidOrder,
  phpAdjustGiftCard,
  nodeRefundTerminalPayment,
  phpChangeOrderStatus,
  nodeReverseTerminalPayment,
  auth,
} from "../data/api";
import { renderRefundReceiptHTML, printReceiptHTML } from "../data/receipt";
import { getLoyaltyConfig } from "../data/tenderData";
import { getTaxMap } from "../data/queries";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";
import NumericInput from "../NumericInput";
import {
  buildItemLevelRefund,
  buildPartialRefund,
  netUnitOf,
  isCashPaidOrder,
  lineDiscountRows,
  cardRefundModeOf,
  deriveRefundModeCaps,
  computeLoyaltyLapse,
  parseOrderLoyaltyJson,
  orderTaxSummary,
  orderDiscountSummary,
  formatOrderDateTime,
  roundHalfUp,
  toNum,
  REFUND_THRESHOLD,
  REFUND_REASONS,
  ORDER_STATUS_REFUNDED,
  ORDER_STATUS_VOIDED,
  refundNcaBaseFactor,
  shouldHideNcaOnSummary,
  surchargeModeFromOrder,
  refundLineFactor,
  rowRefundValueWithLp,
  maxPartialRefundable,
  registerOrderSummary,
  storeCreditRefundDef,
  allToStoreCreditAmount,
} from "../engine";

// In-store order history + refunds — the web port of Android's order list → detail → RefundFragment.
// Refund MODES mirror Android: the money goes back BY HOW IT WAS PAID — Cash, Card (Payroc refund
// first, like the PAX terminal refund), Loyalty points, Gift Card (balance credit) — or any part to
// STORE CREDIT ("all refund in store credit", customer required). Tip + NCA have their own toggles.
// The checked modes must sum to the refund total (±$0.01, Android REFUND_THRESHOLD). When the order
// EARNED loyalty for a customer, the earned points LAPSE proportionally (lapsed_loyalty_point*).
// is_online drives the orderType branch in the engine (In-Store vs online fee handling).
const ymd = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};
// Per-tender gateway-refund guard: once a card tender is successfully refunded at the gateway, the
// result is remembered (sessionStorage) so a RETRY after a partial multi-card failure — or after
// the record call failed — can never charge the same card twice. Cleared when the refund records.
const gwGuardKey = (orderId, paymentId) => `qv_gw_refunded_${orderId}_${paymentId}`;
const readGwGuard = (orderId, paymentId) => {
  try {
    const raw = sessionStorage.getItem(gwGuardKey(orderId, paymentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeGwGuard = (orderId, paymentId, data) => {
  try {
    sessionStorage.setItem(gwGuardKey(orderId, paymentId), JSON.stringify(data));
  } catch {
    /* ignore */
  }
};
const clearGwGuards = (orderId, tenders) => {
  try {
    for (const t of tenders) sessionStorage.removeItem(gwGuardKey(orderId, t.paymentId));
  } catch {
    /* ignore */
  }
};

export default function OrdersModal({ onClose, settings }) {
  const [view, setView] = useState("list"); // list | detail | refund
  const [range, setRange] = useState({ key: "today", start: ymd(new Date()), end: ymd(new Date()) });
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState(null); // null = loading
  const [detail, setDetail] = useState(null); // { cart_data, order_detail, refund_data, split_payments }
  const [busy, setBusy] = useState(false);
  const [loyaltyCfg, setLoyaltyCfg] = useState(null);
  // Android order summary names the default-tax row after the store tax list's DefaultTax entry.
  const [defaultTaxName, setDefaultTaxName] = useState("Default Tax");
  useEffect(() => {
    getTaxMap()
      .then((m) => {
        for (const t of m.values()) if (t.title === "DefaultTax") return setDefaultTaxName(t.displayname || t.displayName || t.title);
      })
      .catch(() => {});
  }, []);

  // refund state
  const [refundKind, setRefundKind] = useState("items"); // "items" (line-item) | "amount" (partial $)
  const [partialAmount, setPartialAmount] = useState(0); // direct $ refund (Android partial refund)
  const [sel, setSel] = useState({}); // line_item_id -> qty to refund now
  const [tipOn, setTipOn] = useState(false);
  const [ncaOn, setNcaOn] = useState(false);
  const [tipEdit, setTipEdit] = useState(null); // Android tietRefundTip — editable up to maxTipAmt (null = full tip)
  // Reason drives SERVER-SIDE inventory restock: anything except "Defective Item" restocks.
  const [reason, setReason] = useState(REFUND_REASONS[0].value);
  const [reasonOther, setReasonOther] = useState("");
  const reasonSent = reason === "Other" ? reasonOther.trim() || "Other" : reason;
  // modes: key -> { on, amount, dirty } — prefills track the total until the cashier edits a field
  const [modes, setModes] = useState({});

  useEffect(() => {
    getLoyaltyConfig().then(setLoyaltyCfg).catch(() => setLoyaltyCfg(null));
  }, []);

  const loadList = useCallback(async (start, end) => {
    setOrders(null);
    try {
      const res = await phpOrderList({ startDate: start, endDate: end });
      const rows = res && Array.isArray(res.result) ? res.result : [];
      // Android (OfflineFragment paging) renders orders/order_list in the SERVER's order — no client
      // re-sort. The old merchant_time string sort scrambled AM/PM rows ("orders sequence galat").
      setOrders(rows);
    } catch {
      setOrders([]);
      ToastifyAlert("Failed to load orders", "error");
    }
  }, []);

  useEffect(() => {
    loadList(range.start, range.end);
  }, [range, loadList]);

  const openDetail = async (orderId) => {
    setBusy(true);
    try {
      const res = await phpOrderDetails(String(orderId).trim());
      if (!res || !res.result || !res.result.order_detail) throw new Error("no detail");
      setDetail(res.result);
      setSel({});
      setTipOn(false);
      setNcaOn(false);
      setTipEdit(null);
      setReason(REFUND_REASONS[0].value);
      setModes({});
      setRefundKind("items");
      setPartialAmount(0);
      setView("detail");
      return true;
    } catch {
      ToastifyAlert("Order not found", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const od = detail && detail.order_detail;
  // Android 8885312b92 (§74a): screens read the mode stamped on the ORDER (coupon_code.is_surcharge;
  // missing ⇒ not dual); the refund REQUEST falls back to the store setting (InStoreRefundRepository:297).
  const orderMode = od ? surchargeModeFromOrder(od, parseOrderLoyaltyJson(od)) : null;
  const isSurcharge = orderMode != null ? orderMode : "0";
  const payloadSurcharge = orderMode != null ? orderMode : settings && settings.isSurcharge;
  const hideNca = shouldHideNcaOnSummary(isSurcharge);
  const dualRefundFactor = od ? refundNcaBaseFactor(isSurcharge, od) : 1;
  // RF-3: a zero-price, zero-tax line keeps factor 1.0 (Android fb3e18957a).
  const rowValue = (item, q) => rowRefundValueWithLp(item, q) * refundLineFactor(item, dualRefundFactor);
  const cart = useMemo(() => (detail && Array.isArray(detail.cart_data) ? detail.cart_data : []), [detail]);
  const refundHistory = useMemo(() => (detail && detail.refund_data) || [], [detail]);
  const splitPayments = useMemo(() => (detail && detail.split_payments) || [], [detail]);
  const isVoided = od ? String(od.m_status) === ORDER_STATUS_VOIDED : false;
  const isOnline = od ? String(od.is_online) === "1" : false;
  const orderType = isOnline ? "Online Order" : "In-Store Order";
  const customerName = od ? String(od.billing_name || od.deliver_name || "").trim() : "";
  const orderNoLabel = od ? `${toNum(od.order_number) > 0 ? `#${od.order_number} · ` : ""}${od.order_id}` : "";

  // A sold gift-card line ("Gift Card(NUMBER)") is NOT line-item refundable — the card's balance is
  // adjusted through the gift-card flow, so POS never lists it among refundable items (Varun
  // 2026-08-27). Same shape buildGiftcardJson issues it as; anchored so a real product isn't caught.
  const refundable = useMemo(
    () =>
      cart.filter(
        (it) => toNum(it.qty) > 0 && !/^Gift Card\(/.test(String((it && it.name) || "")) && String(it && it.is_lottery) !== "1"
      ),
    [cart]
  );
  // Fully refunded = the server's REFUNDED status OR no un-refunded items remain. Mirrors Android's
  // refund gate (InStoreOrderDetailRefundFragment): the refundable list is cartData.filter { isRefunded
  // in ("0","2") } and the terminal state is m_status == REFUNDED — Android NEVER blocks on the
  // amt-based remaining (remainingGrandTotal = amt − refundAmount is display-only there). The old
  // `remaining <= REFUND_THRESHOLD` clause was a web-only addition that wrongly locked a gift-card /
  // loyalty / store-credit-paid order out of refunds: such an order stores amt≈0 (the reduction is
  // subtracted from amt) yet its items are fully refundable (Nilam 2026-08-28 — a custom item paid by
  // gift card would not refund). qty>0 ⟺ Android isRefunded ∈ {"0","2"}.
  const fullyRefunded = od ? String(od.m_status) === ORDER_STATUS_REFUNDED || refundable.length === 0 : false;
  const itemsSelected = useMemo(
    () =>
      refundable
        .map((it) => ({ item: it, sel: toNum(sel[String(it.line_item_id || it.id)]) || 0 }))
        .filter((x) => x.sel > 0),
    [refundable, sel]
  );

  // Tender-based mode caps (Android max*Payout matrix) — recomputed as the selection changes (the
  // line-item loyalty cap depends on which rows are selected).
  const caps = useMemo(
    () => (od ? deriveRefundModeCaps({ orderDetail: od, refundData: refundHistory, splitPayments, itemsSelected, isSurcharge }) : null),
    [od, refundHistory, splitPayments, itemsSelected, isSurcharge]
  );

  const itemsTotal = itemsSelected.reduce((s, x) => s + rowValue(x.item, x.sel), 0);
  // Line-item kind totals from the selected rows; partial kind is a direct $ amount (≤ remaining —
  // tip/NCA are added on top, never carved out of the cap: Android "Maximum Refundable Amount").
  // Android "Maximum Refundable Amount" (RF-7) — tip/NCA are added on top, never carved out.
  const maxRefundable = od ? maxPartialRefundable(od, cart, isSurcharge === "2") : 0;
  const baseAmount = refundKind === "amount" ? Math.min(toNum(partialAmount), Math.max(maxRefundable, 0)) : itemsTotal;
  const goodsTotal = roundHalfUp(baseAmount);
  const tipAmt = tipOn && caps ? roundHalfUp(Math.min(tipEdit != null ? toNum(tipEdit) : caps.tip, caps.tip)) : 0;
  const ncaAmt =
    ncaOn && caps && caps.nca > 0
      ? roundHalfUp(caps.card > 0 ? Math.min(caps.nca, (caps.nca * Math.min(goodsTotal, caps.card)) / caps.card) : caps.nca)
      : 0;
  const togglesTotal = tipAmt + ncaAmt;
  const refundTotal = roundHalfUp(goodsTotal + togglesTotal);

  // Available modes for THIS order (cap > 0; store credit gated on a customer, any amount).
  // Card modes are PER TENDER — each split card row has its own payment_id/cap, so a multi-card
  // order refunds each card by its own referenced Payroc refund.
  const modeDefs = useMemo(() => {
    if (!caps) return [];
    const defs = [];
    if (caps.cash > 0) defs.push({ key: "cash", label: "Cash", cap: caps.cash });
    caps.cardTenders.forEach((t, i) => {
      if (t.cap > 0)
        defs.push({
          key: `card:${t.paymentId || i}`,
          label: `Card${t.last4 ? ` •${t.last4}` : ""}${t.cardType ? ` (${t.cardType})` : ""}`,
          cap: t.cap,
          tender: t,
        });
    });
    // Line-item refunds cap LP at the selected rows' LP; a partial ($) refund caps at the order's
    // remaining LP spent (Android maxLoyaltyAmtPayout branches the same way).
    const lpCap = refundKind === "amount" ? caps.lpSpentRemaining : caps.loyalty;
    // Android :2041-2051 — a line-item refund refunds the points spent on the selected lines FIRST and
    // locks that amount; cash/card then fill the remainder.
    if (lpCap > 0) {
      const lpDef = { key: "loyalty", label: "Loyalty points", cap: lpCap, locked: refundKind === "items" };
      if (refundKind === "items") defs.unshift(lpDef);
      else defs.push(lpDef);
    }
    // Android maxStoreCreditAmt: what the tenders paid minus what was already refunded (field = min(total, max)).
    const scDef = storeCreditRefundDef(caps);
    if (scDef) defs.push(scDef);
    if (caps.giftCard.cap > 0) defs.push({ key: "giftCard", label: `Gift card (${caps.giftCard.number})`, cap: caps.giftCard.cap });
    return defs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caps, refundKind]);
  const capOf = (def) => (def.cap == null ? goodsTotal : def.cap);

  // Android-style prefill: modes fill in DEF ORDER (cash, then each card, …) up to their caps until
  // the whole total is covered — stops adapting once the cashier edits any amount.
  useEffect(() => {
    if (view !== "refund" || !caps) return;
    setModes((prev) => {
      if (Object.values(prev).some((v) => v && v.dirty)) return prev; // cashier took over
      const next = {};
      let left = goodsTotal;
      for (const def of modeDefs) {
        if (def.key === "storeCredit" && modeDefs.length > 1) {
          next[def.key] = { on: false, amount: 0, dirty: false };
          continue; // SC never auto-fills unless it's the only option
        }
        const take = roundHalfUp(def.locked ? capOf(def) : Math.min(left, capOf(def)));
        next[def.key] = { on: take > 0, amount: take, dirty: false };
        left = roundHalfUp(left - take);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, goodsTotal, modeDefs]);

  // a card refund is selected (Android cbRefundCredit) — gates the tip / NCA refund
  const cardOn = modeDefs.some((d) => d.tender && modes[d.key] && modes[d.key].on && toNum(modes[d.key].amount) > 0);
  const modeSum = modeDefs.reduce((s, d) => s + (modes[d.key] && modes[d.key].on ? toNum(modes[d.key].amount) : 0), 0);
  const sumOk = Math.abs(roundHalfUp(modeSum) - goodsTotal) <= REFUND_THRESHOLD;

  const setMode = (k, patch) => setModes((s) => ({ ...s, [k]: { ...(s[k] || { on: false, amount: 0 }), ...patch, dirty: true } }));
  const allToStoreCredit = () => {
    const next = {};
    for (const d of modeDefs) next[d.key] = { on: false, amount: 0, dirty: true };
    next.storeCredit = { on: true, amount: allToStoreCreditAmount(goodsTotal, caps), dirty: true };
    setModes(next);
  };

  // Android InStoreRefundOrderItemAdapter: a Mix&Match line refunds as the WHOLE deal — ticking one
  // bulk-price line ticks EVERY bulk-price line at full qty (and unticking clears them all); the
  // +/− steppers refuse with "Mix n match promotion is applied on this item." A BOGO line does the
  // same for its bogo_id group.
  const dealGroupOf = (it) => (String(it.is_bulk_price) === "1" ? "bulk" : toNum(it.bogo_id) > 0 ? `bogo:${it.bogo_id}` : null);
  const setQty = (it, q) => {
    const id = String(it.line_item_id || it.id);
    const group = dealGroupOf(it);
    if (group) {
      const selected = toNum(sel[id]) > 0;
      if (selected && q > 0) {
        return ToastifyAlert(group === "bulk" ? "Mix n match promotion is applied on this item." : "BOGO promotion is applied on this item.", "error");
      }
      const on = !selected; // first tap = tick the deal, a tap down to 0 = untick the deal
      setSel((s) => {
        const next = { ...s };
        for (const x of refundable) if (dealGroupOf(x) === group) next[String(x.line_item_id || x.id)] = on ? toNum(x.qty) : 0;
        return next;
      });
      return;
    }
    setSel((s) => ({ ...s, [id]: Math.max(0, Math.min(toNum(it.qty), q)) }));
  };
  const selectAll = () => {
    const all = {};
    for (const it of refundable) all[String(it.line_item_id || it.id)] = toNum(it.qty);
    setSel(all);
  };

  const startRefund = () => {
    if (fullyRefunded) return ToastifyAlert("This order is already fully refunded", "error");
    setView("refund");
  };

  const printRefundReceipt = (total, methodSummary) => {
    try {
      const a = auth();
      printReceiptHTML(
        renderRefundReceiptHTML({
          store: { name: a.storeName, address: a.storeAddress, phone: a.storePhone },
          orderId: orderNoLabel,
          dateTime: new Date().toLocaleString(),
          cashier: a.employeeName || "",
          items: itemsSelected.map(({ item, sel: q }) => ({ qty: q, name: item.name, amount: rowValue(item, q) })),
          tipAmount: tipAmt,
          // Android NUPM :3567-3586 — dual price prints no separate NCA row; it is inside the card line.
          ncaAmount: isSurcharge === "2" ? 0 : ncaAmt,
          total,
          method: methodSummary,
          reason: reasonSent,
        })
      );
    } catch {
      /* print blocked — non-fatal, refund already recorded */
    }
  };

  // VOID (Android orders/updateVoidOrder): reverse card charges at the gateway (open batch), then
  // the server flips m_status=5 + cvvResult 'void' and reverses loyalty/etc. Only before any refund.
  const doVoid = async () => {
    if (toNum(od.refund_amount) > 0) return ToastifyAlert("This order already has refunds — use Refund instead of Void", "error");
    if (!window.confirm(`VOID order ${orderNoLabel}? This cancels the entire transaction.`)) return;
    setBusy(true);
    try {
      for (const t of caps.cardTenders) {
        if (!t.paymentId) continue;
        const done = readGwGuard(od.order_id, `void_${t.paymentId}`);
        if (done) continue; // already reversed on a previous attempt
        const gw = await nodeReverseTerminalPayment({ paymentId: t.paymentId, amount: t.cap });
        if (!gw || gw.status !== true) {
          ToastifyAlert(
            `Card reversal failed (${(gw && (gw.reason || gw.message)) || "gateway error"}) — the batch may already be settled. Use Refund instead.`,
            "error"
          );
          return;
        }
        writeGwGuard(od.order_id, `void_${t.paymentId}`, { amount: t.cap });
      }
      const res = await phpVoidOrder(od.order_id);
      if (res && (res.status === true || res.status === "true")) {
        clearGwGuards(od.order_id, caps.cardTenders.map((t) => ({ paymentId: `void_${t.paymentId}` })));
        // mKonnekt: NOT reported from the client — Android e80cae8ca3/40cf8a5c4f moved void/refund
        // reporting server-side (the mkonnekt_order_sync sweep reads the order tables). Owner decision 10-01.
        ToastifyAlert("Order voided", "success");
        await openDetail(od.order_id);
      } else {
        ToastifyAlert((res && res.message) || "Void failed — card reversals (if any) will not re-run on retry", "error");
      }
    } catch (e) {
      ToastifyAlert("Void failed: " + (e && e.message ? e.message : e), "error");
    } finally {
      setBusy(false);
    }
  };

  const doRefund = async () => {
    // Validation FIRST with explicit messages — the button is never silently disabled.
    if (refundKind === "items" && !itemsSelected.length && !tipOn && !ncaOn)
      return ToastifyAlert("Select at least one item to refund", "error");
    if (refundKind === "amount" && refundTotal <= 0) return ToastifyAlert("Enter the amount to refund", "error");
    if (!sumOk)
      return ToastifyAlert(
        `The refund methods add up to ${fmt(modeSum)} but the items total is ${fmt(goodsTotal)} — adjust the amounts to match`,
        "error"
      );
    const a = auth();
    const amt = (k) => (modes[k] && modes[k].on ? roundHalfUp(toNum(modes[k].amount)) : 0);
    // Per-tender card portions (each refunds by its OWN Payroc payment id).
    const cardPortions = modeDefs
      .filter((d) => d.tender && amt(d.key) > 0)
      .map((d) => ({ tender: d.tender, amount: amt(d.key), label: d.label }));
    const cardAmt = roundHalfUp(cardPortions.reduce((s, p) => s + p.amount, 0));
    // Android RefundFragment :642-652 / :3625: tip + NCA refund only through the card, on top of it.
    if (togglesTotal > 0 && cardPortions.length === 0)
      return ToastifyAlert("Select Refund by Credit to refund the tip / Non Cash Adjustment", "error");
    if (cardPortions.length > 0) cardPortions[0].gateway = roundHalfUp(cardPortions[0].amount + togglesTotal);
    const cashAmt = amt("cash");
    const loyaltyAmt = amt("loyalty");
    const scAmt = amt("storeCredit");
    const gcAmt = amt("giftCard");
    const card = cardAmt > 0 ? cardRefundModeOf(od) : null;
    const paymentMode = isCashPaidOrder(od) ? "cash" : cardRefundModeOf(od).paymentMode;
    // Loyalty LAPSE: order earned points for a customer → claw back proportionally to what leaves in
    // money (credit+cash), like Android (cap = credit+cash entered). A partial ($) refund has no
    // selected rows — base over ALL remaining earn-eligible rows, still capped at credit+cash.
    const lapseItems = refundKind === "amount" ? refundable.map((it) => ({ item: it, sel: toNum(it.qty) })) : itemsSelected;
    const lapse =
      caps.hasCustomer && caps.lpEarned.flag && loyaltyCfg
        ? computeLoyaltyLapse({
            itemsSelected: lapseItems,
            pointsPerDollar: loyaltyCfg.pointsPerDollar,
            redemptionValue: loyaltyCfg.dollarPerPoint,
            // Android :1777-1820 / :1906 — cap/basis = credit + (dual ? NCA : 0) + cash; never more than earned.
            capAmount: cardAmt + cashAmt + (isSurcharge === "2" ? ncaAmt : 0),
            maxPoints: caps.lpEarned.points,
            partialAmount: refundKind === "amount" ? cardAmt + cashAmt + (isSurcharge === "2" ? ncaAmt : 0) : null,
          })
        : { points: 0, amount: 0 };
    // Android NUPM :3567 — under dual price the card line carries its NCA ("Credit Card(-$credit+nca)").
    const firstCardKey = cardPortions.length ? modeDefs.find((d) => d.tender && amt(d.key) > 0).key : null;
    const methodSummary = modeDefs
      .filter((d) => amt(d.key) > 0)
      .map((d) => `${d.label} ${fmt(amt(d.key) + (isSurcharge === "2" && d.key === firstCardKey ? ncaAmt : 0))}`)
      .join(" + ");
    if (!window.confirm(`Refund ${fmt(refundTotal)}?\n${methodSummary}`)) return;
    setBusy(true);
    try {
      // 1) money back on the card(s) FIRST (Android PAX-refund-first), SEQUENTIALLY per tender.
      // Each success is guarded in sessionStorage so a retry after a partial failure (or a failed
      // record call) reuses the stored result instead of charging the gateway again.
      const gwResults = [];
      for (const p of cardPortions) {
        const done = readGwGuard(od.order_id, p.tender.paymentId);
        const gwAmount = p.gateway != null ? p.gateway : p.amount;
        if (done && Number(done.amount) === Number(gwAmount)) {
          gwResults.push(done.raw || {});
          continue; // already refunded on a previous attempt — never re-run
        }
        const gw = await nodeRefundTerminalPayment({
          paymentId: p.tender.paymentId,
          amount: gwAmount,
          description: `Refund order ${od.order_id}`,
        });
        if (!gw || gw.status !== true) {
          const doneOnes = cardPortions
            .filter((x) => readGwGuard(od.order_id, x.tender.paymentId))
            .map((x) => `${x.label} ${fmt(x.amount)}`)
            .join(", ");
          ToastifyAlert(
            `${p.label} refund was declined (${(gw && (gw.reason || gw.message)) || "gateway error"}).` +
              (doneOnes ? ` Already refunded: ${doneOnes} — press Refund again after fixing; refunded cards will NOT be charged twice.` : ""),
            "error"
          );
          return;
        }
        writeGwGuard(od.order_id, p.tender.paymentId, { amount: gwAmount, raw: gw.raw || {} });
        gwResults.push(gw.raw || {});
      }
      const paxDetails = gwResults.length === 0 ? "" : JSON.stringify(gwResults.length === 1 ? gwResults[0] : gwResults);
      // 2) gift-card portion: credit the card's balance back (Android setAdjustGiftCardAmount)
      if (gcAmt > 0 && caps.giftCard.number) {
        const adj = await phpAdjustGiftCard({ number: caps.giftCard.number, amount: gcAmt, orderId: od.order_id });
        if (!adj || !(adj.status === true || adj.status === "true")) {
          ToastifyAlert("Gift-card balance credit failed — refund not recorded. Card portions already refunded are protected on retry.", "error");
          return;
        }
      }
      // 3) record the refund. loyalty_point = points equivalent of the LP amount (spent-ratio).
      const ctx = {
        orderId: od.order_id,
        merchantId: a.merchantId,
        employeeId: a.employeeId,
        groupId: a.groupId,
        customerId: od.customer_id || "",
        advId: "",
        shiftSetting: String((settings && settings.raw && (settings.raw.shift_assign ?? settings.raw.shiftAssign)) || "1"), // Android shiftAssign ?: "1"
        paymentDateTime: formatOrderDateTime(new Date()),
      };
      const common = {
        orderDetail: od,
        modes: { credit: cardAmt, cash: cashAmt, loyalty: loyaltyAmt, storeCredit: scAmt, giftCard: gcAmt },
        tip: { on: tipAmt > 0, amount: tipAmt },
        nca: { on: ncaAmt > 0, amount: ncaAmt },
        reason: reasonSent,
        paymentMode,
        cardType: card ? card.cardType : "",
        refundPaxDetails: paxDetails,
        loyaltyPoints: String(roundHalfUp(loyaltyAmt * caps.lpRatio)),
        lapsed: { points: lapse.points, amount: lapse.amount },
        ctx,
      };
      let fields;
      let totalRefundAmount;
      // Whether this refund clears the WHOLE order. Both builders already decide it (the engine sets
      // m_status=7 off the same answer) and it is unit-tested there, so it is read from them rather
      // than re-derived here — the two must never disagree.
      if (refundKind === "amount") {
        // PARTIAL ($) refund — Android update_partial_refund
        const built = buildPartialRefund({ ...common, cartData: cart, refundData: refundHistory, amount: baseAmount, maxLoyaltyAmt: caps.lpSpentRemaining, refundableCap: maxRefundable });
        fields = built.fields;
        totalRefundAmount = built.partialTotal;
      } else {
        // RF-5: Android totalItemCount = cartData.size (ALL rows); RF-1: payload mode = order ?? store.
        const built = buildItemLevelRefund({ ...common, items: itemsSelected, totalItemCount: cart.length, orderType, isSurcharge: payloadSurcharge });
        fields = built.fields;
        totalRefundAmount = built.totalRefundAmount;
      }
      const res = refundKind === "amount" ? await phpPartialRefund(fields) : await phpItemLevelRefund(fields);
      if (res && (res.status === true || res.status === "true")) {
        // Android RefundFragment :1733-1770 — an amount-tab refund that clears the remaining grand total
        // marks the order REFUNDED via change_order_status (update_partial_refund never sets m_status).
        if (refundKind === "amount") {
          const lj = parseOrderLoyaltyJson(od);
          const remainingGT =
            toNum(od.amt) -
            toNum(od.refund_amount) +
            (toNum(od.discount) - toNum(lj.coupon_code_amt) - toNum(lj.bogo_discount) - Math.abs(toNum(lj.mix_match_discount)) - toNum(lj.store_credit_amt_spent));
          if (Math.trunc(Math.abs(refundTotal - remainingGT) * 100) / 100 <= REFUND_THRESHOLD) {
            phpChangeOrderStatus(od.order_id, ORDER_STATUS_REFUNDED).catch(() => {});
          }
        }
        clearGwGuards(od.order_id, cardPortions.map((p) => p.tender)); // recorded — guards done their job
        // mKonnekt refund reporting is server-side (Android e80cae8ca3) — no client call.
        ToastifyAlert(`Refunded ${fmt(totalRefundAmount)}`, "success");
        printRefundReceipt(totalRefundAmount, methodSummary || "Cash");
        await openDetail(od.order_id);
      } else if (cardAmt > 0) {
        ToastifyAlert(
          `The card(s) were refunded ${fmt(cardAmt)} but recording it failed (${(res && res.message) || "server error"}) — press Refund again (refunded cards will NOT be charged twice), or record it in the admin panel.`,
          "error"
        );
      } else {
        ToastifyAlert((res && res.message) || "Refund failed", "error");
      }
    } catch (e) {
      ToastifyAlert("Refund failed: " + (e && e.message ? e.message : e), "error");
    } finally {
      setBusy(false);
    }
  };

  // Per-unit net paid. Floors at 0 rather than Math.abs — see engine/refund netUnitOf.
  const netLine = (it) => netUnitOf(it);

  // What came off each saved line (manual / manufacturer / coupon). Lives in the engine because the
  // two amount scales — per-unit `discount_amt` vs the mkonnekt LINE TOTAL — are easy to confuse and
  // are unit-tested there. See engine/orderDetail.js.

  const RANGES = [
    { key: "today", label: "Today", start: () => ymd(new Date()), end: () => ymd(new Date()) },
    { key: "yesterday", label: "Yesterday", start: () => daysAgo(1), end: () => daysAgo(1) },
    { key: "week", label: "Last 7 days", start: () => daysAgo(6), end: () => ymd(new Date()) },
  ];

  const title = view === "list" ? "Orders" : view === "detail" ? `Order ${orderNoLabel}` : `Refund — ${orderNoLabel}`;
  return (
    <Modal title={title} onClose={onClose} width={640}>
      {view === "list" && (
        <>
          {/* Search by order id → direct lookup (works for ANY date) */}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...m.input, flex: 1 }}
              placeholder="Search by order ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search.trim() && openDetail(search)}
            />
            <button style={{ ...m.primary, width: 110, marginTop: 0 }} disabled={busy || !search.trim()} onClick={() => openDetail(search)}>
              Search
            </button>
          </div>
          {/* ⚠️ WRAPS, and the chips never shrink. Unwrapped, the four controls needed ~470px and a
              phone gave 336: flex shrank the pills, "Last 7 days" broke onto three lines inside a
              34px-tall pill, and the date field was squeezed off the right edge. The row is allowed
              to take a second line instead — vertical space is the thing a phone actually has. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, alignItems: "center" }}>
            {RANGES.map((r) => (
              <button
                key={r.key}
                style={{ ...sChip, ...(range.key === r.key ? sChipOn : {}) }}
                onClick={() => setRange({ key: r.key, start: r.start(), end: r.end() })}
              >
                {r.label}
              </button>
            ))}
            <input
              type="date"
              aria-label="Custom date"
              style={{ ...m.input, flex: "1 1 148px", minWidth: 148, maxWidth: 190, height: 38, marginLeft: "auto" }}
              value={range.key === "custom" ? range.start : ""}
              onChange={(e) => e.target.value && setRange({ key: "custom", start: e.target.value, end: e.target.value })}
            />
          </div>
          <div style={{ marginTop: 12, maxHeight: 340, overflow: "auto" }}>
            {orders === null && <div style={sMuted}>Loading…</div>}
            {Array.isArray(orders) && orders.length === 0 && <div style={sMuted}>No in-store orders in this range</div>}
            {Array.isArray(orders) &&
              orders.map((o) => (
                <div key={o.id || o.order_id} onClick={() => !busy && openDetail(o.order_id)} style={sRow}>
                  <div style={sRowMain}>
                    <div style={{ fontWeight: 500 }}>
                      {toNum(o.order_number) > 0 ? `#${o.order_number} · ` : ""}
                      {o.order_id}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {o.merchant_time || o.date_time}
                      {String(o.name || "").trim() ? ` · 👤 ${String(o.name).trim()}` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                    <div style={{ fontWeight: 500 }}>{fmt(o.amt)}</div>
                    {toNum(o.refund_amount) > 0 && <div style={{ fontSize: 12, color: "#DC2626" }}>−{fmt(o.refund_amount)} refunded</div>}
                  </div>
                </div>
              ))}
          </div>
        </>
      )}

      {view === "detail" && od && (
        <>
          {customerName && <div style={{ marginBottom: 8, fontWeight: 500, color: "var(--accent)" }}>👤 {customerName}</div>}
          <div style={{ maxHeight: 280, overflow: "auto" }}>
            {cart.map((it) => (
              <div key={String(it.line_item_id || it.id)} style={{ ...sRow, cursor: "default" }}>
                <div style={sRowMain}>
                  <div style={{ fontWeight: 500 }}>{it.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    {toNum(it.qty)} x {fmt(netLine(it))}
                    {toNum(it.refund_qty) > 0 ? ` · ${toNum(it.refund_qty)} refunded` : ""}
                  </div>
                  {lineDiscountRows(it).map((d, di) => (
                    // `sub` rows are the per-allowance breakdown under a manufacturer discount —
                    // indented and quieter, so the total stays the line the eye lands on.
                    <div
                      key={di}
                      style={
                        d.sub
                          ? { fontSize: 11, color: "var(--ink-2)", fontWeight: 500, paddingLeft: 12 }
                          : { fontSize: 12, color: "#16A34A", fontWeight: 500 }
                      }
                    >
                      {d.label} (−{fmt(d.amount)})
                    </div>
                  ))}
                </div>
                <div style={{ fontWeight: 500, flex: "0 0 auto" }}>{fmt(netLine(it) * toNum(it.qty))}</div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10, marginTop: 4, fontSize: 14, color: "var(--ink-2)" }}>
            <Row l="Paid via" r={`${od.payment_id || ""}${od.card_type && od.card_type !== od.payment_id ? ` (${od.card_type})` : ""}${splitPayments.length ? ` · split ×${splitPayments.length}` : ""}`} />
            {/* Android InStoreOrderDetailFragmentNewVersion summary — values from the SAVED order. */}
            {(() => {
              const sm = registerOrderSummary(od, cart, refundHistory, isSurcharge, parseOrderLoyaltyJson(od) || {});
              return (
                <>
                  <Row l="Gross Sale" r={fmt(sm.grossSale)} />
                  {sm.loyaltyRedeemed > 0 && <Row l={`Loyalty Points Redeemed (${sm.loyaltyRedeemedPoints.toFixed(2)})`} r={`−${fmt(sm.loyaltyRedeemed)}`} />}
                  <Row l="Net Sale" r={fmt(sm.netSale)} />
                  {sm.otherFees > 0 && <Row l="Other Fees" r={fmt(sm.otherFees)} />}
                  <Row l="Grand Total" r={fmt(sm.grandTotal)} />
                  {sm.changeDue > 0 && <Row l="Change Due" r={fmt(sm.changeDue)} muted />}
                  {sm.totalRefund > 0 && (
                    <>
                      <Row l="Total Tax Refunded" r={`−${fmt(sm.totalTaxRefunded)}`} red muted />
                      <Row l="Total Refund" r={`−${fmt(sm.totalRefund)}`} red />
                      <Row l="Grand Total After Refund" r={fmt(sm.grandTotalAfterRefund)} />
                    </>
                  )}
                </>
              );
            })()}
            {/* Android order summary discount rollup: BOGO + Mix N' Match (+ coupon when OTD) as "Discount",
                then a "Coupon (name)" sub-row and a "Custom Discount" sub-row. Per-line deal rows show above. */}
            {(() => {
              const ds = orderDiscountSummary(od, cart, parseOrderLoyaltyJson(od) || {});
              return (
                <>
                  {ds.discount > 0 && <Row l="Discount" r={`−${fmt(ds.discount)}`} />}
                  {ds.coupon && <Row l={ds.coupon.label} r={`−${fmt(ds.coupon.amount)}`} muted />}
                  {ds.custom != null && <Row l="Custom Discount" r={`−${fmt(ds.custom)}`} muted />}
                </>
              );
            })()}
            {/* Android order summary "Taxes" block: per tax → name · rate (3 dp) / taxable amt / sales tax due, then the total. */}
            {(() => {
              const ts = orderTaxSummary(od, cart, defaultTaxName);
              return (
                <>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "var(--ink-2)", letterSpacing: "0.04em", textTransform: "uppercase" }}>Taxes</div>
                  {ts.rows.map((t, i) => (
                    <React.Fragment key={i}>
                      <Row l={`${t.name} · ${t.rate}`} r={fmt(t.due)} />
                      <Row l="Taxable Amt." r={fmt(t.taxable)} muted />
                    </React.Fragment>
                  ))}
                  <Row l="Total tax" r={fmt(ts.total)} />
                </>
              );
            })()}
            {toNum(od.tip) > 0 && <Row l="Tip" r={fmt(od.tip)} />}
            {toNum(od.cash_discounting) > 0 && !hideNca && <Row l="Non-Cash Adjustment" r={fmt(od.cash_discounting)} />}
            {/* Android InStoreOrderDetail: "Loyalty Points Awarded" from the loyalty JSON in coupon_code, shown when > 0 */}
            {toNum((parseOrderLoyaltyJson(od) || {}).loyalty_point_earned) > 0 && (
              <Row l="Loyalty Points Awarded" r={toNum((parseOrderLoyaltyJson(od) || {}).loyalty_point_earned).toFixed(2)} />
            )}
            {refundHistory.map((r, i) => (
              <Row key={i} l={`Refund ${r.created_at || ""}${r.reason ? ` — ${r.reason}` : ""}`} r={`−${fmt(r.amount)}`} red muted />
            ))}
          </div>
          <button style={m.ghost} onClick={() => setView("list")}>← Back to orders</button>
          {isVoided ? (
            <div style={{ ...sMuted, color: "#DC2626", fontWeight: 500 }}>VOIDED</div>
          ) : (
            <>
              <button style={{ ...m.primary, opacity: fullyRefunded ? 0.5 : 1 }} disabled={busy || fullyRefunded} onClick={startRefund}>
                {fullyRefunded ? "Fully refunded" : "Refund…"}
              </button>
              {toNum(od.refund_amount) <= 0 && !fullyRefunded && (
                <button style={{ ...m.ghost, color: "#DC2626", borderColor: "#DC2626" }} disabled={busy} onClick={doVoid}>
                  Void transaction
                </button>
              )}
            </>
          )}
        </>
      )}

      {view === "refund" && od && caps && (
        <>
          {/* Operator context: which order/customer these line items belong to */}
          <div style={{ marginBottom: 8, padding: "8px 12px", background: "var(--panel-2)", borderRadius: 10, fontSize: 13 }}>
            <b>{orderNoLabel}</b>
            {customerName ? <span style={{ color: "var(--accent)" }}> · 👤 {customerName}</span> : ""}
            <span style={{ color: "var(--ink-3)" }}> · {orderType}</span>
          </div>
          {/* Line-item refund vs Android's PARTIAL (direct $ amount) refund */}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            {/* Line-item and amount refunds are mutually exclusive (Android isLineItemRefund) — switching
                clears the other mode's input so a partial $ refund can never also carry item selections. */}
            <button style={{ ...sChip, ...(refundKind === "items" ? sChipOn : {}) }} onClick={() => { setRefundKind("items"); setPartialAmount(0); }}>Line items</button>
            <button style={{ ...sChip, ...(refundKind === "amount" ? sChipOn : {}) }} onClick={() => { setRefundKind("amount"); setSel({}); }}>Amount ($)</button>
          </div>
          {refundKind === "amount" && (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 14, color: "var(--ink)", fontWeight: 500 }}>Refund amount</span>
              <NumericInput
                style={{ ...m.input, width: 130, height: 44 }}
                value={partialAmount}
                decimals={2}
                onChange={(v) => setPartialAmount(Math.min(toNum(v), maxRefundable))}
                autoFocus
              />
              <span style={{ fontSize: 12, color: "var(--ink-3)" }}>Maximum Refundable Amount: {fmt(maxRefundable)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, visibility: refundKind === "items" ? "visible" : "hidden" }}>
            <span style={{ fontSize: 13, color: "var(--ink-2)" }}>Pick quantities to refund</span>
            <button style={sLink} onClick={selectAll}>Select all</button>
          </div>
          <div style={{ maxHeight: 190, overflow: "auto", display: refundKind === "items" ? "block" : "none" }}>
            {refundable.map((it) => {
              const id = String(it.line_item_id || it.id);
              const q = toNum(sel[id]) || 0;
              return (
                <div key={id} style={{ ...sRow, cursor: "default", flexWrap: "wrap" }}>
                  <div style={{ ...sRowMain, flex: "1 1 150px" }}>
                    <div style={{ fontWeight: 500 }}>{it.name}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{fmt(rowValue(it, 1))}/unit incl. tax · {toNum(it.qty)} left</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
                    <button style={sStep} onClick={() => setQty(it, q - 1)} aria-label={`Refund one less ${it.name}`}>−</button>
                    <span style={{ minWidth: 22, textAlign: "center", fontWeight: 500 }}>{q}</span>
                    <button style={sStep} onClick={() => setQty(it, q + 1)} aria-label={`Refund one more ${it.name}`}>+</button>
                  </div>
                  <div style={{ minWidth: 76, textAlign: "right", fontWeight: 500, flex: "0 0 auto" }}>{fmt(rowValue(it, q))}</div>
                </div>
              );
            })}
          </div>
          {/* Android RefundFragment :642-652 — tip / NCA refund only through the card (enabled while a
              card refund is on); the tip amount is editable up to the tip paid. */}
          {caps.tip > 0 && caps.cardTenders.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ ...sCheck, flex: 1 }}>
                <input type="checkbox" checked={tipOn} disabled={!cardOn} onChange={(e) => setTipOn(e.target.checked)} /> Refund tip (max {fmt(caps.tip)})
              </label>
              {tipOn && (
                <NumericInput
                  style={{ ...m.input, width: 100, height: 40 }}
                  value={tipEdit != null ? tipEdit : caps.tip}
                  decimals={2}
                  onChange={(v) => setTipEdit(Math.min(toNum(v), caps.tip))}
                />
              )}
            </div>
          )}
          {caps.nca > 0 && caps.cardTenders.length > 0 && (
            <label style={sCheck}>
              <input type="checkbox" checked={ncaOn} disabled={!cardOn} onChange={(e) => setNcaOn(e.target.checked)} /> Refund Non-Cash Adjustment ({fmt(ncaOn ? ncaAmt : caps.nca)})
            </label>
          )}

          {/* Refund BY MODE (Android checkboxes): amounts must add up to the total */}
          <div style={{ marginTop: 10, borderTop: "1px solid var(--line)", paddingTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "var(--ink-2)" }}>Refund to</span>
              {storeCreditRefundDef(caps) && (
                <button style={sLink} onClick={allToStoreCredit}>All to store credit</button>
              )}
            </div>
            {modeDefs.map((def) => {
              const st = modes[def.key] || { on: false, amount: 0 };
              const cap = capOf(def);
              return (
                <div key={def.key} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "4px 0" }}>
                  <label style={{ ...sCheck, marginTop: 0, flex: "1 1 150px", minWidth: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!st.on}
                      disabled={!!def.locked}
                      onChange={(e) => setMode(def.key, { on: e.target.checked, amount: e.target.checked ? Math.min(goodsTotal, cap) : 0 })}
                    />
                    {def.label}
                    <span style={{ color: "var(--ink-3)", fontSize: 12 }}> max {fmt(cap)}</span>
                  </label>
                  <NumericInput
                    style={{ ...m.input, width: 110, height: 44, marginLeft: "auto", opacity: st.on ? 1 : 0.5 }}
                    value={st.amount}
                    decimals={2}
                    disabled={!!def.locked}
                    onChange={(v) => setMode(def.key, { on: true, amount: Math.min(toNum(v), cap) })}
                  />
                </div>
              );
            })}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: sumOk ? "#16A34A" : "#DC2626", fontWeight: 500 }}>
              <span>Methods total</span>
              <span>{fmt(modeSum)} / {fmt(goodsTotal)}{togglesTotal > 0 ? ` (+${fmt(togglesTotal)} tip/NCA on card)` : ""}</span>
            </div>
          </div>

          {caps.hasCustomer && caps.lpEarned.flag && (
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
              This order earned loyalty points — the earned points lapse proportionally on refund.
            </div>
          )}
          {/* Reason drives inventory restock server-side: everything except "Defective Item" puts
              the refunded quantity back in stock (Process_refund_api rule). */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            <span style={{ fontSize: 14, color: "var(--ink)", fontWeight: 500 }}>Reason</span>
            <select style={{ ...m.input, flex: 1, height: 42 }} value={reason} onChange={(e) => setReason(e.target.value)}>
              {REFUND_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.value}
                  {r.restock ? " — restocks inventory" : " — does NOT restock"}
                </option>
              ))}
            </select>
          </div>
          {reason === "Other" && (
            <input
              style={{ ...m.input, marginTop: 8, height: 42 }}
              placeholder="Enter the reason"
              value={reasonOther}
              maxLength={120}
              onChange={(e) => setReasonOther(e.target.value)}
            />
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 17, fontWeight: 800 }}>
            <span>Refund total</span>
            <span>{fmt(refundTotal)}</span>
          </div>
          <button style={m.ghost} onClick={() => setView("detail")}>← Back</button>
          {/* NEVER silently disabled — clicking with a problem shows the exact message (doRefund validates) */}
          <button style={{ ...m.primary, background: "#DC2626" }} disabled={busy} onClick={doRefund}>
            {busy ? "Refunding…" : `Refund ${fmt(refundTotal)}`}
          </button>
        </>
      )}
    </Modal>
  );
}

function Row({ l, r, red, muted }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: muted ? "var(--ink-3)" : undefined, fontSize: muted ? 12 : undefined }}>
      <span>{l}</span>
      <span style={red ? { color: "#DC2626", fontWeight: 500 } : { fontWeight: 500 }}>{r}</span>
    </div>
  );
}

const sRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 10, marginBottom: 8, cursor: "pointer" };
// A flex child will not shrink past its longest word unless it is told it may. Product names and
// order ids are long enough to have pushed these rows off the right edge of a phone.
const sRowMain = { minWidth: 0, flex: "1 1 auto", overflowWrap: "anywhere" };
const sMuted = { color: "var(--ink-3)", padding: 16, textAlign: "center" };
// 28px was a mouse target. These set the refunded quantity — a mis-tap here refunds the wrong amount.
const sStep = { width: 40, height: 40, flex: "0 0 auto", borderRadius: 10, border: "1px solid var(--line)", background: "var(--panel-2)", cursor: "pointer", fontSize: 18 };
const sCheck = { display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 14, color: "var(--ink)" };
const sLink = { border: "none", background: "none", color: "var(--accent)", cursor: "pointer", fontWeight: 500 };
// minHeight (not height) + nowrap + no shrink: a pill must be free to be as wide as its own label,
// and must never be asked to fit a longer label into a shorter box.
const sChip = { minHeight: 40, padding: "0 14px", borderRadius: 20, border: "1px solid var(--line)", background: "var(--panel-2)", color: "var(--ink)", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", flex: "0 0 auto" };
const sChipOn = { background: "var(--accent-soft)", border: "2px solid var(--accent)", color: "var(--accent)" };
